import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  type ErrorComponentProps,
  Outlet,
  useRouteContext,
} from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { lazy, Suspense, useSyncExternalStore } from "react";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Toaster } from "@/components/ui/toast";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useAuthCallback } from "@/hooks/use-auth-callback";
import { useAutoScrollInput } from "@/hooks/use-auto-scroll-input";
import { useBrowserSession } from "@/hooks/use-browser-session";
import { useVisualViewport } from "@/hooks/use-visual-viewport";
import { sessionRevision, subscribeToSession } from "@/lib/session-request-scope";

const TanStackRouterDevtools = import.meta.env.PROD
  ? () => null
  : lazy(() =>
      import("@tanstack/router-devtools").then((m) => ({
        default: m.TanStackRouterDevtools,
      })),
    );

interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  errorComponent: RootErrorBoundary,
  pendingComponent: RootPending,
});

function RootLayout() {
  const { queryClient } = useRouteContext({ from: "__root__" });
  useAuthCallback(queryClient);
  useBrowserSession(queryClient);
  useVisualViewport();
  useAutoScrollInput();
  const revision = useSyncExternalStore(subscribeToSession, sessionRevision);

  return (
    <TooltipProvider key={revision}>
      <Outlet />
      <Toaster />
      <Suspense>
        <TanStackRouterDevtools position="bottom-right" />
      </Suspense>
    </TooltipProvider>
  );
}

function RootErrorBoundary({ error, reset }: ErrorComponentProps) {
  return (
    <div className="flex min-h-[var(--visual-vh,100vh)] items-center justify-center bg-paper">
      <EmptyState
        pose="shrug"
        title="Something went wrong"
        description={error instanceof Error ? error.message : "An unexpected error occurred."}
        action={<Button onClick={reset}>Try again</Button>}
      />
    </div>
  );
}

function RootPending() {
  return (
    <div className="flex min-h-[var(--visual-vh,100vh)] items-center justify-center bg-paper">
      <Loader2 className="size-8 animate-spin text-electric" />
    </div>
  );
}
